import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** Safety & privacy → Muted words. Messages containing these words are hidden
 *  from the user's chats. Saved instantly on add/remove. */
export default function SettingsMutedWords() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [words, setWords] = useState(user?.muted_words || []);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/users/me/muted-words", { words: next });
      setWords(data.muted_words);
      await refresh();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const add = (e) => {
    e.preventDefault();
    const w = input.trim().toLowerCase();
    if (!w) return;
    if (words.includes(w)) { setInput(""); return; }
    setInput("");
    save([...words, w]);
  };

  const remove = (w) => save(words.filter((x) => x !== w));

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-muted-page">
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="muted-back" aria-label="Back">
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[28px] tracking-tight">{t("settings.mutedWords")}</h1>
        </div>
      </div>

      <div className="px-5 pt-5">
        <p className="text-sm text-[#7C828D] mb-4">{t("settings.mutedWordsDesc")}</p>

        <form onSubmit={add} className="flex gap-2 mb-5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={40}
            placeholder={t("settings.mutedWordsAdd")}
            className="flex-1 bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7]"
            data-testid="muted-word-input"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="bg-[#00CED1] text-black font-black px-4 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center gap-1"
            data-testid="muted-word-add"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" strokeWidth={3} />}
          </button>
        </form>

        {words.length === 0 ? (
          <p className="text-sm text-[#B9BDC7]" data-testid="muted-empty">{t("settings.mutedWordsEmpty")}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {words.map((w) => (
              <span key={w} className="inline-flex items-center gap-1.5 bg-[#F4F1E8] border-2 border-[#121212] rounded-full pl-3 pr-1.5 py-1.5 font-bold text-sm" data-testid={`muted-word-${w}`}>
                {w}
                <button onClick={() => remove(w)} disabled={busy} className="w-5 h-5 rounded-full bg-[#121212] text-white flex items-center justify-center" data-testid={`muted-remove-${w}`} aria-label={`Remove ${w}`}>
                  <X className="w-3 h-3" strokeWidth={3} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
